// src/views/ResetPasswordView.jsx
// Página para capturar el token de Supabase y permitir cambiar la contraseña
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../config/supabase';
import { AlertCircle, CheckCircle, Lock, ArrowLeft, Eye, EyeOff } from 'lucide-react';

const ResetPasswordView = () => {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sessionReady, setSessionReady] = useState(false);
  const [checking, setChecking] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Capturar el token de la URL automáticamente
  useEffect(() => {
    const checkSession = async () => {
      try {
        // Supabase detecta el hash token automáticamente con detectSessionInUrl: true
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session) {
          setSessionReady(true);
        } else {
          // Esperar un momento por si el token aún se está procesando
          setTimeout(async () => {
            const { data: { session: retrySession } } = await supabase.auth.getSession();
            setSessionReady(!!retrySession);
            setChecking(false);
          }, 1500);
          return;
        }
      } catch (err) {
        console.error('Error verificando sesión:', err);
      }
      setChecking(false);
    };

    checkSession();

    // También escuchar cambios de auth (el token de recovery dispara PASSWORD_RECOVERY)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        setSessionReady(true);
        setChecking(false);
      }
    });

    return () => subscription?.unsubscribe();
  }, []);

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    setError('');

    if (!password) {
      setError('Ingresa tu nueva contraseña');
      return;
    }
    if (password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    if (password !== passwordConfirm) {
      setError('Las contraseñas no coinciden');
      return;
    }

    setLoading(true);
    try {
      const { error: updateError } = await supabase.auth.updateUser({
        password: password,
      });

      if (updateError) {
        setError(updateError.message);
        setLoading(false);
        return;
      }

      setSuccess(true);
      // Cerrar sesión para que entre con la nueva contraseña
      await supabase.auth.signOut();
    } catch (err) {
      setError('Error al actualizar la contraseña. Intenta nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  // Estado: verificando token
  if (checking) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.spinnerWrap}>
            <div style={styles.spinner} />
            <p style={styles.checkingText}>Verificando enlace de recuperación...</p>
          </div>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // Estado: token inválido o expirado
  if (!sessionReady && !checking) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.iconWrap}>
            <AlertCircle size={48} color="#ef4444" />
          </div>
          <h2 style={styles.title}>Enlace inválido o expirado</h2>
          <p style={styles.subtitle}>
            El enlace de recuperación ya fue usado o ha expirado. 
            Solicita uno nuevo desde la página de login.
          </p>
          <button
            onClick={() => navigate('/')}
            style={styles.btnPrimary}
          >
            Volver al inicio
          </button>
        </div>
      </div>
    );
  }

  // Estado: éxito
  if (success) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.iconWrap}>
            <CheckCircle size={48} color="#22c55e" />
          </div>
          <h2 style={styles.title}>¡Contraseña actualizada!</h2>
          <p style={styles.subtitle}>
            Tu contraseña ha sido cambiada exitosamente. 
            Ya puedes iniciar sesión con tu nueva contraseña.
          </p>
          <button
            onClick={() => navigate('/')}
            style={styles.btnPrimary}
          >
            Ir a iniciar sesión
          </button>
        </div>
      </div>
    );
  }

  // Estado: formulario de nueva contraseña
  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <button
          type="button"
          onClick={() => navigate('/')}
          style={styles.backBtn}
          aria-label="Volver"
        >
          <ArrowLeft size={20} />
        </button>

        <div style={styles.iconWrap}>
          <Lock size={40} color="#18181b" />
        </div>

        <h2 style={styles.title}>Nueva contraseña</h2>
        <p style={styles.subtitle}>Ingresa tu nueva contraseña para tu cuenta</p>

        {error && (
          <div style={styles.errorBox}>
            <AlertCircle size={16} />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleUpdatePassword} style={styles.form}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Nueva contraseña</label>
            <div style={styles.inputWrap}>
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Mínimo 6 caracteres"
                style={styles.input}
                disabled={loading}
                autoFocus
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={styles.eyeBtn}
                tabIndex={-1}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Confirmar contraseña</label>
            <input
              type={showPassword ? 'text' : 'password'}
              value={passwordConfirm}
              onChange={(e) => setPasswordConfirm(e.target.value)}
              placeholder="Repite tu contraseña"
              style={styles.input}
              disabled={loading}
            />
          </div>

          <button
            type="submit"
            style={{ ...styles.btnPrimary, opacity: loading ? 0.7 : 1 }}
            disabled={loading}
          >
            {loading ? 'Actualizando...' : 'Actualizar contraseña'}
          </button>
        </form>

        <p style={styles.footer}>
          © {new Date().getFullYear()} CopaGimnasta
        </p>
      </div>
    </div>
  );
};

// ─── Estilos inline (consistentes con tu diseño) ───
const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
    padding: '20px',
  },
  card: {
    background: '#fff',
    borderRadius: '16px',
    padding: '40px 32px',
    maxWidth: '420px',
    width: '100%',
    boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
    position: 'relative',
  },
  backBtn: {
    position: 'absolute',
    top: '16px',
    left: '16px',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#6b7280',
    padding: '4px',
  },
  iconWrap: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '16px',
  },
  title: {
    textAlign: 'center',
    fontSize: '1.4rem',
    fontWeight: '700',
    color: '#18181b',
    margin: '0 0 8px',
  },
  subtitle: {
    textAlign: 'center',
    fontSize: '0.9rem',
    color: '#6b7280',
    margin: '0 0 24px',
    lineHeight: '1.5',
  },
  errorBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    background: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '8px',
    padding: '10px 14px',
    color: '#dc2626',
    fontSize: '0.85rem',
    marginBottom: '16px',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  label: {
    fontSize: '0.85rem',
    fontWeight: '600',
    color: '#374151',
  },
  inputWrap: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },
  input: {
    width: '100%',
    padding: '10px 14px',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    fontSize: '0.95rem',
    outline: 'none',
    transition: 'border-color 0.2s',
    boxSizing: 'border-box',
  },
  eyeBtn: {
    position: 'absolute',
    right: '10px',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#9ca3af',
    padding: '4px',
    display: 'flex',
  },
  btnPrimary: {
    width: '100%',
    padding: '12px',
    background: '#18181b',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '0.95rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background 0.2s',
  },
  spinnerWrap: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
    padding: '20px 0',
  },
  spinner: {
    width: '36px',
    height: '36px',
    border: '3px solid #e5e7eb',
    borderTopColor: '#18181b',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  checkingText: {
    color: '#6b7280',
    fontSize: '0.9rem',
  },
  footer: {
    textAlign: 'center',
    fontSize: '0.68rem',
    color: '#94a3b8',
    marginTop: '24px',
    letterSpacing: '0.02em',
  },
};

export default ResetPasswordView;
